// The client's file: documents the firm chose to publish to them, plus what
// they sent us themselves. Everything else on the matter stays internal, and
// the server enforces that - this screen only shows what it is given.

import { useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClientPortalDocument } from "@duralex/shared";
import {
  SESSION_EXPIRED,
  UPLOAD_ACCEPT,
  PORTAL_UPLOAD_MAX_BYTES,
  downloadClientDocument,
  fetchClientDocuments,
  getClientToken,
  uploadClientDocument,
} from "../../api/clientPortal";
import { PortalHeader } from "../../components/portal/PortalHeader";
import { formatPortalDate } from "../../components/portal/portalDate";
import { useExpiredSession } from "../../components/portal/portalSession";

const MAX_MB = Math.round(PORTAL_UPLOAD_MAX_BYTES / 1024 / 1024);

// "system" means a Gmail or Drive pull, but the client did not send it and
// does not know our pipelines: what is true for them is that the firm shared it.
const SOURCE_LABEL: Record<ClientPortalDocument["uploadedBy"], string> = {
  you: "You sent this",
  attorney: "Shared by your legal team",
  system: "Shared by your legal team",
};

function formatDate(iso: string | null): string {
  return formatPortalDate(iso) ?? "";
}

export default function PortalDocuments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = getClientToken();
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const documentsKey = ["portal", "documents", token];
  const { data, isLoading, error } = useQuery({
    queryKey: documentsKey,
    queryFn: fetchClientDocuments,
    retry: false,
  });
  const expired = useExpiredSession(error);

  function report(e: unknown) {
    const message = e instanceof Error ? e.message : "";
    if (message === SESSION_EXPIRED) {
      navigate("/portal/login", { replace: true });
      return;
    }
    setFailure(message || "Something went wrong. Please try again.");
  }

  const upload = useMutation({
    mutationFn: uploadClientDocument,
    onSuccess: (message) => {
      setConfirmation(message);
      void queryClient.invalidateQueries({ queryKey: documentsKey });
    },
    onError: report,
  });

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Cleared so that picking the same file again still raises a change.
    e.target.value = "";
    if (!file) return;

    setFailure(null);
    setConfirmation(null);
    if (file.size > PORTAL_UPLOAD_MAX_BYTES) {
      setFailure(`That file is larger than ${MAX_MB} MB. Please call our office and we will help.`);
      return;
    }
    upload.mutate(file);
  }

  function onDownload(doc: ClientPortalDocument) {
    setFailure(null);
    setConfirmation(null);
    downloadClientDocument(doc.id, doc.name).catch(report);
  }

  return (
    <main className="portal">
      <PortalHeader />

      <div className="portal-body">
        <section className="portal-card">
          <h2>Send us a document</h2>
          <p className="portal-note">
            Photos of pages are fine, as long as the whole page is readable.
          </p>
          <label className="portal-field portal-upload">
            <span>Choose a file</span>
            <input
              type="file"
              aria-label="Choose a file"
              accept={UPLOAD_ACCEPT}
              onChange={onPick}
              disabled={upload.isPending}
            />
            <small>PDF, Word, or a photo. Up to {MAX_MB} MB.</small>
          </label>
          {upload.isPending && <p className="portal-note">Sending your file...</p>}
          {confirmation && <p className="portal-ok">{confirmation}</p>}
          {failure && (
            <p className="portal-bad" role="alert">
              {failure}
            </p>
          )}
        </section>

        <section className="portal-card">
          <h2>Your documents</h2>
          {isLoading || expired ? (
            <p className="portal-note">Loading your documents...</p>
          ) : error || !data ? (
            <p className="portal-note">
              We could not load your documents right now. Please try again, or call our office.
            </p>
          ) : data.length === 0 ? (
            <p className="portal-note">
              Nothing here yet. Anything you send us, and anything your legal team shares with you,
              will appear here.
            </p>
          ) : (
            <ul className="portal-list">
              {data.map((d) => (
                <li key={d.id}>
                  <div>
                    <span className="portal-doc-name">{d.name}</span>
                    <span className="portal-doc-meta">
                      {SOURCE_LABEL[d.uploadedBy]}
                      {formatDate(d.uploadedAt) && ` - ${formatDate(d.uploadedAt)}`}
                    </span>
                  </div>
                  <button className="portal-link-btn" onClick={() => onDownload(d)}>
                    Download
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
